import React from 'react';
import { MapPinned } from 'lucide-react';
import { useAppSelector } from 'src/store';
import {
    getAppRoutesAvailability,
    getProjectRoutesAvailability,
    getSamplesRoutesAvailability,
    getSurveyRoutesAvailability,
    getMapRoutesAvailability,
} from 'src/routes/state/routesSelector';
import { useLocation } from 'react-router-dom';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import useSampleManifestAnalytics from 'src/app/samples/sample-manifest/hooks/useSampleManifestAnalytics';
import { projectRouteSettings } from 'src/app/projects/Projects';
import { samplesRouteSettings } from 'src/app/samples/Samples';
import { surveyRouteSettings } from 'src/app/surveys/Survey';
import { mapRouteSettings } from 'src/app/maps/Maps';
import { appRouteSettings } from 'src/routes/AppRoutes';
import NestedSidebarMenuItem, { NestedSidebarMenuSubItem, isSubGroup } from './NestedSidebarMenuItem';
import useEcologyHubMenuItem from './useEcologyHubMenuItem';
import useTablesMenuItem from './useTablesMenuItem';

const NaturePerformanceMenu = () => {
    const appRoutesAvailability = useAppSelector(getAppRoutesAvailability);
    const projectRoutesAvailability = useAppSelector(getProjectRoutesAvailability);
    const samplesRoutesAvailability = useAppSelector(getSamplesRoutesAvailability);
    const surveyRoutesAvailability = useAppSelector(getSurveyRoutesAvailability);
    const mapRoutesAvailability = useAppSelector(getMapRoutesAvailability);
    const navigate = useAppNavigation();
    const { pathname } = useLocation();
    const { trackSampleManifestLandingFromSiteMenu } = useSampleManifestAnalytics();
    const ecologyHubItem = useEcologyHubMenuItem();
    const tablesItem = useTablesMenuItem();

    if (!appRoutesAvailability.projects) {
        return null;
    }

    const onSampleManifestClick = () => {
        trackSampleManifestLandingFromSiteMenu();
        navigate.toSampleManifest();
    };
    const sampleManifestItemPath = '/samples' + samplesRouteSettings.sampleManifest.path.replace('/*', '');

    const subItems = (
        [
            projectRoutesAvailability.siteSummary && {
                title: 'Site Summary',
                testId: 'site-summary-menu-button',
                onClick: () => navigate.toSiteSummary(),
                isActive: pathname.startsWith('/projects/site-summary'),
            },
            appRoutesAvailability.survey &&
                surveyRoutesAvailability.surveyDesignSummary && {
                    title: 'Survey design',
                    testId: 'survey-design-menu-button',
                    onClick: () => navigate.toSurveyDesignSummary(),
                    isActive:
                        pathname === '/survey' + surveyRouteSettings.surveyDesignSummary.path ||
                        pathname + '/' === '/survey' + surveyRouteSettings.surveyDesignSummary.path,
                },
            appRoutesAvailability.map &&
                mapRoutesAvailability.speciesMap && {
                    title: mapRouteSettings.speciesMap.name,
                    testId: 'species-map-menu-item',
                    onClick: navigate.toSpeciesMap,
                    isActive: pathname.includes('/map' + mapRouteSettings.speciesMap.path),
                },
            appRoutesAvailability.map &&
                mapRoutesAvailability.habitatMap && {
                    title: mapRouteSettings.habitatMap.name,
                    testId: 'habitat-map-menu-item',
                    onClick: navigate.toHabitatMap,
                    isActive: pathname.includes('/map' + mapRouteSettings.habitatMap.path),
                },
            projectRoutesAvailability.freemiumSpeciesInsights && {
                title: projectRouteSettings.freemiumSpeciesInsights.name,
                testId: 'species-insights-menu-item',
                onClick: navigate.toFreemiumSpeciesInsights,
                isActive:
                    pathname === '/projects' + projectRouteSettings.freemiumSpeciesInsights.path ||
                    pathname + '/' === '/projects' + projectRouteSettings.freemiumSpeciesInsights.path,
            },
            appRoutesAvailability.dashboard && {
                title: 'Dashboard',
                testId: 'dashboard-menu-button',
                onClick: () => navigate.toDashboard(),
                isActive: pathname.startsWith(appRouteSettings.dashboard.path),
            },
            ecologyHubItem,
            projectRoutesAvailability.siteFiles && {
                title: 'Files',
                testId: 'files-menu-button',
                onClick: () => navigate.toSiteFiles(),
                isActive: pathname.startsWith('/projects/site-files'),
            },
            tablesItem,
            appRoutesAvailability.samples &&
                samplesRoutesAvailability.sampleManifest && {
                    title: 'Sample Manifest',
                    testId: 'sample-manifest-menu-button',
                    onClick: onSampleManifestClick,
                    isActive: pathname.startsWith(sampleManifestItemPath),
                },
        ] as (NestedSidebarMenuSubItem | false | null)[]
    ).filter((item): item is NestedSidebarMenuSubItem => Boolean(item));

    if (subItems.length === 0) {
        return null;
    }

    const isActive = subItems.some(item => (isSubGroup(item) ? item.subItems.some(subItem => subItem.isActive) : item.isActive));

    return (
        <NestedSidebarMenuItem
            label='Nature Performance'
            testId='nature-performance-menu-group-button'
            icon={<MapPinned />}
            isActive={isActive}
            defaultOpen={isActive}
            subItems={subItems}
        />
    );
};

export default NaturePerformanceMenu;
